import React, { FC, useEffect, useRef, useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useButton, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { Box, Button, GreenButton, Input } from '~Styles';
import DateTimePicker from '~ComponentsImproved/DateTimePicker';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import * as actions from '~actions';
import { BookingLanguage, BookingSource } from '../../../../../__queries__';
import { useBookings } from '~Providers/BookingsProvider';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';
import { Locale } from '~Types';
import useModal from '~ComponentsImproved/Modal/useModal';
import TableSelectModal from '../TableSelectModal';
import { CreateBooking, CreateBookingVariables } from './__queries__';

// Map frontend locale to backend BookingLanguage enum
const localeToBookingLanguage = (locale: Locale): BookingLanguage => {
  const map: Record<Locale, BookingLanguage> = {
    en: BookingLanguage.EN,
    se: BookingLanguage.SV,
    no: BookingLanguage.NO,
    es: BookingLanguage.ES,
    dk: BookingLanguage.SV, // Danish not supported, fallback to Swedish
    tr: BookingLanguage.SV, // Turkish not supported, fallback to Swedish
  };
  return map[locale] || BookingLanguage.EN;
};

const CREATE_BOOKING = gql`
  mutation CreateBooking($input: CreateBookingInput!) {
    createBooking(input: $input) {
      booking {
        id
      }
      error
      message
      success
    }
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  text-align: center;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

// 1-15 as quick-select cells, 16th cell is a custom input
const GUEST_OPTIONS = Array.from({ length: 15 }, (_, i) => ({
  id: String(i + 1),
  name: String(i + 1),
}));

const GuestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  width: 100%;
`;

const CustomGuestInput = styled(Input)<{ active: boolean }>`
  min-height: unset;
  padding: 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const StyledGuestButton = styled(Button)<{ active: boolean }>`
  min-height: unset;
  min-width: unset;
  padding: 8px;
  border-radius: 8px;
  font-size: 14px;
  text-transform: none;
  color: ${({ active }) => (active ? 'var(--colorBlack' : 'var(--colorBlueGray200)')};
  background-color: ${({ active }) =>
    active ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray1100)'};
  border: 1px solid ${({ active }) => (active ? 'var(--colorUIBlue)' : 'var(--colorBlueGray900)')};
`;

const DatePickerWrapper = styled(Box)`
  padding: 8px;
  background-color: var(--colorBlueGray1100);
  border: 1px solid var(--colorBlueGray900);
  border-radius: 8px;
`;

const CancelButton = styled(Button)`
  max-width: 160px;
  white-space: nowrap;
`;

const AssignTableButton = styled(Button)`
  background-color: transparent;
  border: 1px dashed var(--colorBlueGray700);
  color: var(--colorBlueGray200);
  font-size: 13px;
  min-height: unset;
  padding: 8px 12px;
  border-radius: 8px;
  text-transform: none;
`;

const TableChipsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const TableChip = styled.span`
  background-color: var(--colorBlueGray1000);
  border: 1px solid var(--colorUIYellow);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--colorWhite);
`;

interface GuestCellProps {
  value: string;
  label: string;
  active: boolean;
  onSelect: (value: string) => void;
}

const GuestCell: FC<GuestCellProps> = ({ value, label, active, onSelect }) => {
  const button = useButton({
    onClick: () => onSelect(value),
  });

  return (
    <StyledGuestButton {...button} active={active}>
      {label}
    </StyledGuestButton>
  );
};

interface Props {
  onClose: () => void;
  onComplete?: (bookingId: string, date: string) => void;
}

const NewBookingForm: FC<Props> = ({ onClose, onComplete }) => {
  const { i18n, locale } = useI18n();
  const venueId = useSelector((state) => state.venueId) as string;
  const logger = useLogger('new-booking-form');
  const dispatch = useDispatch();
  const bookings = useBookings();

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const commentRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [partySize, setPartySize] = useState<string | null>(null);
  const [customGuests, setCustomGuests] = useState('');
  const [isCustomActive, setIsCustomActive] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<string | null>(null);
  const [manualTableIds, setManualTableIds] = useState<string[]>([]);
  const [manualTableCodes, setManualTableCodes] = useState<string[]>([]);

  const tableSelectModal = useModal();

  const [createBooking] = useMutation<CreateBooking, CreateBookingVariables>(CREATE_BOOKING);

  const cancelButton = useButton({
    onClick: onClose,
  });

  const assignTableButton = useButton({
    onClick: tableSelectModal.toggle,
  });

  // Check required fields: guestName, partySize, and dateTime
  useEffect(() => {
    const checkDisabled = () => {
      const disabled =
        loading || !nameRef.current?.value?.trim() || !selectedDateTime || !partySize;
      setIsSubmitDisabled(disabled);
    };

    checkDisabled();
    const interval = setInterval(checkDisabled, 100);
    return () => clearInterval(interval);
  }, [loading, partySize, selectedDateTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    const guestName = nameRef.current?.value?.trim() || '';
    const guestEmail = emailRef.current?.value?.trim() || undefined;
    const guestPhone = phoneRef.current?.value?.trim() || undefined;
    const comment = commentRef.current?.value?.trim() || undefined;

    // Extract date and time from the DateTimePicker ISO string
    const pickedDateTime = DateTime.fromISO(selectedDateTime!);
    const date = pickedDateTime.toFormat('yyyy-MM-dd');
    const startTime = pickedDateTime.toFormat('HH:mm');

    try {
      const res = await createBooking({
        variables: {
          input: {
            venueId,
            date,
            startTime,
            partySize: Number(partySize),
            guestName,
            guestEmail: guestEmail || undefined,
            guestPhone: guestPhone || undefined,
            comment: comment || undefined,
            source: BookingSource.BOOKING,
            language: localeToBookingLanguage(locale),
            idempotencyKey: uuid(),
            tableIds: manualTableIds.length > 0 ? manualTableIds : undefined,
          },
        },
      });

      if (!res.data?.createBooking?.success) {
        const errorCode = res.data?.createBooking?.error;

        if (errorCode === 'ERROR_PARTY_SIZE_TOO_LARGE') {
          throw new Error(i18n('Bookings.ErrorPartySizeTooLarge'));
        }

        throw new Error(res.data?.createBooking?.message || i18n('Bookings.ErrorCreatingBooking'));
      }

      logger.info('Booking created successfully', {
        bookingId: res.data.createBooking.booking?.id,
        guestName,
        partySize,
        date,
        startTime,
      });

      dispatch(actions.addToast(i18n('Bookings.BookingCreatedToast', guestName), true));

      // Refetch bookings for the selected date
      await bookings.fetchBookingsForDate(date);

      const bookingId = res.data.createBooking.booking?.id;
      if (bookingId && onComplete) {
        onComplete(bookingId, date);
      }

      onClose();
    } catch (err) {
      logger.error(`Failed to create booking: ${err ? err.toString() : ''}`);
      setError(err ? err.toString() : i18n('Bookings.ErrorCreatingBooking'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormWrapper autoComplete="off" onSubmit={handleSubmit}>
      {/* Guest name (required) */}
      <Input
        ref={nameRef}
        id="guestName"
        name="guestName"
        placeholder={i18n('Bookings.GuestNamePlaceholder')}
        required
        dark
        padding="small"
        fluid
        marginBottom="xs"
      />

      {/* Email (required) */}
      <Input
        ref={emailRef}
        id="guestEmail"
        name="guestEmail"
        type="email"
        placeholder={i18n('Bookings.EmailPlaceholder')}
        dark
        padding="small"
        fluid
        marginBottom="xs"
      />

      <Input
        ref={phoneRef}
        id="guestPhone"
        name="guestPhone"
        type="tel"
        placeholder={i18n('Bookings.PhonePlaceholder')}
        dark
        padding="small"
        fluid
        marginBottom="xs"
      />

      {/* Comment (optional) */}
      <Input
        ref={commentRef}
        id="comment"
        name="comment"
        placeholder={i18n('Bookings.CommentPlaceholder')}
        dark
        padding="small"
        fluid
        marginBottom="xs"
      />

      <DatePickerWrapper fluid marginTop="xs" marginBottom="xs">
        <DateTimePicker onChange={setSelectedDateTime} dark fullWidth quarterHourSteps size="sm" />
      </DatePickerWrapper>

      <Box fluid marginBottom="xs">
        <Paragraph level={4} color="var(--colorBlueGray200)" bottom={8}>
          {i18n('Bookings.GuestsLabel')}
        </Paragraph>
        <GuestGrid>
          {GUEST_OPTIONS.map((option) => (
            <GuestCell
              key={option.id}
              value={option.id}
              label={option.name}
              active={!isCustomActive && partySize === option.id}
              onSelect={(val) => {
                setIsCustomActive(false);
                setCustomGuests('');
                setPartySize(val);
              }}
            />
          ))}
          <CustomGuestInput
            active={isCustomActive}
            dark
            type="number"
            min={1}
            max={100}
            placeholder="16+"
            value={customGuests}
            onFocus={() => setIsCustomActive(true)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              setCustomGuests(val);
              setIsCustomActive(true);
              const num = Number(val);
              if (num >= 1 && num <= 100) {
                setPartySize(val);
              } else {
                setPartySize(null);
              }
            }}
          />
        </GuestGrid>
      </Box>

      {/* Assign to table manually */}
      <Box fluid marginBottom="xs" display="flex" alignItems="center" gap="xs">
        <AssignTableButton {...assignTableButton}>
          {i18n('Bookings.AssignToTableManually')}
        </AssignTableButton>
        {manualTableCodes.length > 0 && (
          <TableChipsWrapper>
            {manualTableCodes.map((code, idx) => (
              <TableChip key={manualTableIds[idx]}>{code}</TableChip>
            ))}
          </TableChipsWrapper>
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="row"
        gap="small"
        alignItems="center"
        fluid
        marginTop="small">
        <CancelButton {...cancelButton}>{i18n('General.Cancel')}</CancelButton>
        <GreenButton fluid type="submit" disabled={isSubmitDisabled}>
          {loading ? (
            <Loader size="sm" color="var(--colorWhite)" />
          ) : (
            i18n('Bookings.CreateBooking')
          )}
        </GreenButton>
      </Box>

      {error && (
        <ErrorBox
          fluid
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginTop="small">
          <Paragraph level={5} bottom={0} color="var(--colorUIRed)">
            {error}
          </Paragraph>
        </ErrorBox>
      )}
      {/* Table selection modal */}
      <TableSelectModal
        isShowing={tableSelectModal.isShowing}
        toggle={tableSelectModal.toggle}
        selectedTableIds={manualTableIds}
        onConfirm={(tableIds, tableCodes) => {
          setManualTableIds(tableIds);
          setManualTableCodes(tableCodes);
          logger.info('Manually assigned tables for new booking', { tableIds, tableCodes });
          tableSelectModal.toggle();
        }}
      />
    </FormWrapper>
  );
};

export default NewBookingForm;
